import type { components } from "../api/schema";

// The pure transcript formatters live here so the canvas modules
// (TranscriptContent/ResultsRail/TranscriptNotes) and any future answer
// surface share one implementation.

export type AnswerLocator = NonNullable<
  components["schemas"]["AnswerResponse"]["locator"]
>;

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

// Table date cells render date-only for density; the full timestamp rides the
// cell's `title`. Detail surfaces (KeyValue rows, audit log) keep
// `formatDateTime`.
export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
  }).format(new Date(value));
}

export function relativeTime(value: string): string {
  const seconds = Math.round((Date.parse(value) - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (Math.abs(seconds) < 60) {
    return formatter.format(seconds, "second");
  }
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) {
    return formatter.format(minutes, "minute");
  }
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) {
    return formatter.format(hours, "hour");
  }
  return formatter.format(Math.round(hours / 24), "day");
}

export function formatMonthDay(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
  }).format(new Date(value));
}

export function formatJsonValue(value: unknown): string {
  if (value == null) {
    return "-";
  }
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value);
}

// Pretty-printed JSON for `Code block` cells (eval errors, audit diffs).
export function formatJsonBlock(value: unknown): string {
  if (value == null) {
    return "-";
  }
  return JSON.stringify(value, null, 2);
}

function formatNullableNumber(value: number | null): string {
  return value == null ? "all" : value.toString();
}

export function formatLocator(locator: AnswerLocator | null): string {
  if (!locator) {
    return "-";
  }

  switch (locator.kind) {
    case "whole_transcript":
      return "whole_transcript";
    case "audio_span":
      return `audio_span ${locator.start_ms.toString()}-${locator.end_ms_exclusive.toString()} ms`;
    case "utterance_range":
      return `utterance_range ${locator.start_index.toString()}-${locator.end_index_exclusive.toString()}`;
    case "char_span":
      return `char_span ${formatNullableNumber(
        locator.utterance_index,
      )}:${locator.start_char.toString()}-${locator.end_char_exclusive.toString()}`;
    case "trace_node":
      return `trace_node ${locator.node_id}`;
  }
}

export function formatMilliseconds(value: number | null): string {
  if (value == null) {
    return "?";
  }
  return `${value.toString()} ms`;
}

// `m:ss` clock time for a millisecond offset (conversation timestamps, the
// results-rail evidence window, the identity-row duration).
export function formatClock(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${String(minutes)}:${seconds}`;
}

export const END_REASON_LABELS: Record<string, string> = {
  agent_ended: "Agent ended",
  audio_exhausted: "Audio exhausted",
  cancelled: "Cancelled",
  idle_timeout: "Idle timeout",
  max_duration: "Max duration",
  persona_ended: "Persona ended",
  pipeline_error: "Pipeline error",
  script_completed: "Script completed",
};

export function displayEndReason(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  return END_REASON_LABELS[value] ?? value;
}

export function displayValue(value: string | null | undefined): string {
  return value && value.length > 0 ? value : "-";
}
